<template>
  <div class="dp" v-click-outside="close">
    <div class="dp__input" @click="onInputClick">
      <v-icon size="18" class="dp__icon" @click.stop="toggle">{{ icon }}</v-icon>
      <input
        ref="input"
        :value="typedText"
        class="dp__field"
        @input="onType"
        @focus="onFocus"
        @keydown.enter="onEnter"
      />
      <v-icon v-if="value || typedText" size="16" class="dp__clear" @click.stop="clear">mdi-close</v-icon>
    </div>
    <label class="dp__label" :class="{ 'dp__label--up': open || value || typing || focused }">{{ label }}</label>

    <transition name="dp-drop">
      <div v-if="open" class="dp__dropdown">
        <div class="dp__head">
          <button class="dp__arrow" @click.stop="prevMonth">
            <v-icon size="20">mdi-chevron-left</v-icon>
          </button>
          <button class="dp__title" @click.stop="toggleYearView">
            {{ monthNames[viewMonth] }} {{ viewYear }}
          </button>
          <button class="dp__arrow" @click.stop="nextMonth">
            <v-icon size="20">mdi-chevron-right</v-icon>
          </button>
        </div>

        <div v-if="yearView" class="dp__years-wrap">
          <button type="button" class="dp__years-nav" @click.stop.prevent="prevYearPage">
            <v-icon size="16">mdi-chevron-up</v-icon>
          </button>
          <div class="dp__years">
            <button
              type="button"
              v-for="y in yearRange"
              :key="y"
              class="dp__year"
              :class="{ 'dp__year--active': y === viewYear }"
              @click.stop="pickYear(y)"
            >{{ y }}</button>
          </div>
          <button type="button" class="dp__years-nav" @click.stop.prevent="nextYearPage">
            <v-icon size="16">mdi-chevron-down</v-icon>
          </button>
        </div>

        <template v-else>
          <div class="dp__weekdays">
            <span v-for="d in dayNames" :key="d">{{ d }}</span>
          </div>
          <div class="dp__grid">
            <span
              v-for="(cell, i) in cells"
              :key="i"
              class="dp__cell"
              :class="{
                'dp__cell--empty': !cell,
                'dp__cell--today': cell && cell.today,
                'dp__cell--selected': cell && cell.selected,
                'dp__cell--preview': cell && cell.preview && !cell.selected,
                'dp__cell--disabled': cell && cell.disabled,
              }"
              @click.stop="cell && !cell.disabled && pickDay(cell.day)"
            >{{ cell ? cell.day : '' }}</span>
          </div>
        </template>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'DatePicker',
  props: {
    value: { type: String, default: null },
    label: { type: String, default: '' },
    icon: { type: String, default: 'mdi-calendar' },
    min: { type: String, default: null },
    max: { type: String, default: null },
  },
  data() {
    const now = new Date()
    return {
      open: false,
      yearView: false,
      typing: false,
      focused: false,
      rawTyped: '',
      viewMonth: now.getMonth(),
      viewYear: now.getFullYear(),
      yearPageStart: now.getFullYear() - 6,
      monthNames: [
        'Yanvar', 'Fevral', 'Mart', 'Aprel', 'May', 'Iyun',
        'Iyul', 'Avgust', 'Sentabr', 'Oktabr', 'Noyabr', 'Dekabr'
      ],
      dayNames: ['Du', 'Se', 'Cho', 'Pa', 'Ju', 'Sha', 'Ya'],
    }
  },
  computed: {
    typedText() {
      if (this.typing) return this.rawTyped
      if (!this.value) return ''
      const [y, m, d] = this.value.split('-')
      return `${d}.${m}.${y}`
    },
    yearRange() {
      const years = []
      for (let y = this.yearPageStart; y < this.yearPageStart + 12; y++) years.push(y)
      return years
    },
    typingDay() {
      if (!this.typing) return -1
      const raw = this.rawTyped.replace(/\D/g, '')
      if (raw.length >= 2) return parseInt(raw.slice(0, 2))
      return -1
    },
    cells() {
      const firstDay = new Date(this.viewYear, this.viewMonth, 1)
      let startDow = firstDay.getDay() - 1
      if (startDow < 0) startDow = 6
      const daysInMonth = new Date(this.viewYear, this.viewMonth + 1, 0).getDate()
      const today = new Date()
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`

      const arr = []
      for (let i = 0; i < startDow; i++) arr.push(null)
      for (let d = 1; d <= daysInMonth; d++) {
        const dateStr = `${this.viewYear}-${String(this.viewMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`
        arr.push({
          day: d,
          today: dateStr === todayStr,
          selected: dateStr === this.value,
          preview: this.typing && d === this.typingDay && d <= daysInMonth,
          disabled: (this.min && dateStr < this.min) || (this.max && dateStr > this.max),
        })
      }
      return arr
    },
  },
  watch: {
    value(v) {
      if (v) {
        const [y, m] = v.split('-').map(Number)
        this.viewYear = y
        this.viewMonth = m - 1
      }
    }
  },
  methods: {
    onInputClick() {
      this.$refs.input.focus()
    },
    onFocus() {
      this.focused = true
      this.open = true
      this.yearView = false
      this.typing = false
      if (this.value) {
        const [y, m] = this.value.split('-').map(Number)
        this.viewYear = y
        this.viewMonth = m - 1
      }
    },
    toggle() {
      this.open = !this.open
      this.yearView = false
      if (this.open && this.value) {
        const [y, m] = this.value.split('-').map(Number)
        this.viewYear = y
        this.viewMonth = m - 1
      }
    },
    close() {
      this.open = false
      this.yearView = false
      this.typing = false
      this.focused = false
      this.rawTyped = ''
    },
    onType(e) {
      const raw = e.target.value.replace(/\D/g, '').slice(0, 8)
      let formatted = raw
      if (raw.length > 4) formatted = raw.slice(0, 2) + '.' + raw.slice(2, 4) + '.' + raw.slice(4)
      else if (raw.length > 2) formatted = raw.slice(0, 2) + '.' + raw.slice(2)
      this.typing = true
      this.rawTyped = formatted
      this.open = true
      this.$nextTick(() => { e.target.value = formatted })

      // Update calendar view in realtime
      if (raw.length >= 4) {
        const mm = parseInt(raw.slice(2, 4))
        if (mm >= 1 && mm <= 12) this.viewMonth = mm - 1
      }
      if (raw.length >= 6) {
        const yyyy = parseInt(raw.slice(4))
        if (yyyy >= 1900 && yyyy <= 2100) this.viewYear = yyyy
      }
      if (raw.length === 8) this.tryParseTyped(raw)
    },
    onEnter() {
      const raw = this.rawTyped.replace(/\D/g, '')
      if (raw.length === 8) this.tryParseTyped(raw)
    },
    tryParseTyped(digits) {
      const dd = parseInt(digits.slice(0, 2))
      const mm = parseInt(digits.slice(2, 4))
      const yyyy = parseInt(digits.slice(4, 8))
      if (mm >= 1 && mm <= 12 && dd >= 1 && dd <= 31 && yyyy >= 1900 && yyyy <= 2100) {
        const v = `${yyyy}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`
        this.typing = false
        this.rawTyped = ''
        this.$emit('input', v)
        this.open = false
      }
    },
    prevMonth() {
      if (this.viewMonth === 0) { this.viewMonth = 11; this.viewYear-- }
      else this.viewMonth--
    },
    nextMonth() {
      if (this.viewMonth === 11) { this.viewMonth = 0; this.viewYear++ }
      else this.viewMonth++
    },
    toggleYearView() {
      this.yearView = !this.yearView
      if (this.yearView) this.yearPageStart = this.viewYear - 4
    },
    prevYearPage() {
      this.yearPageStart -= 12
    },
    nextYearPage() {
      this.yearPageStart += 12
    },
    pickYear(y) {
      this.viewYear = y
      this.yearView = false
    },
    pickDay(day) {
      const v = `${this.viewYear}-${String(this.viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
      this.$emit('input', v)
      this.open = false
    },
    clear() {
      this.typing = false
      this.rawTyped = ''
      this.$emit('input', null)
      this.$emit('clear')
    },
  },
}
</script>

<style scoped>
.dp { position: relative; }

.dp__input {
  display: flex; align-items: center; gap: 6px;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  padding: 8px 10px;
  cursor: pointer;
  background: transparent;
  transition: border-color .15s;
  min-height: 40px;
}
.dp__input:hover { border-color: #cbd5e1; }
.dp:focus-within .dp__input,
.dp__input:has(~ .dp__dropdown) { border-color: #00274c; border-width: 2px; padding: 7px 9px; }

.dp__icon { color: #94a3b8 !important; flex-shrink: 0; }
.dp__clear { color: #94a3b8 !important; cursor: pointer; flex-shrink: 0; }
.dp__clear:hover { color: #64748b !important; }

.dp__field {
  border: none; outline: none; background: transparent;
  font-size: 13px; font-weight: 600; color: #0f172a;
  width: 100%; cursor: pointer;
}

.dp__label {
  position: absolute; top: 50%; left: 34px;
  transform: translateY(-50%);
  font-size: 13px; font-weight: 500; color: #94a3b8;
  pointer-events: none;
  transition: all .15s;
  background: #fff;
  padding: 0 4px;
}
.dp__label--up {
  top: -1px; left: 10px;
  transform: translateY(-50%);
  font-size: 11px;
  color: #00274c;
}

/* Dropdown */
.dp__dropdown {
  position: absolute; top: calc(100% + 4px); left: 0;
  z-index: 2147483647;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 12px 40px rgba(0,0,0,.12);
  padding: 12px;
  min-width: 280px;
  user-select: none;
}
.dp-drop-enter-active, .dp-drop-leave-active { transition: opacity .15s, transform .15s; }
.dp-drop-enter, .dp-drop-leave-to { opacity: 0; transform: translateY(-6px); }

/* Header */
.dp__head {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 8px;
}
.dp__arrow {
  appearance: none; border: none; background: none;
  cursor: pointer; padding: 4px; border-radius: 8px;
  color: #475569;
  display: flex; align-items: center; justify-content: center;
}
.dp__arrow:hover { background: #f1f5f9; }
.dp__title {
  appearance: none; border: none; background: none;
  font-weight: 700; font-size: 14px; color: #0f172a;
  cursor: pointer; padding: 4px 8px; border-radius: 8px;
}
.dp__title:hover { background: #f1f5f9; }

/* Weekdays */
.dp__weekdays {
  display: grid; grid-template-columns: repeat(7, 1fr);
  text-align: center; margin-bottom: 4px;
}
.dp__weekdays span {
  font-size: 11px; font-weight: 700; color: #94a3b8;
  padding: 4px 0; text-transform: uppercase;
}

/* Day grid */
.dp__grid {
  display: grid; grid-template-columns: repeat(7, 1fr);
  gap: 2px; text-align: center;
}
.dp__cell {
  width: 36px; height: 36px; margin: auto;
  display: flex; align-items: center; justify-content: center;
  border-radius: 10px; font-size: 13px; font-weight: 600;
  cursor: pointer; color: #1e293b;
  transition: background .1s;
}
.dp__cell:hover:not(.dp__cell--empty):not(.dp__cell--disabled):not(.dp__cell--selected) {
  background: #f1f5f9;
}
.dp__cell--empty { cursor: default; }
.dp__cell--today { border: 1.5px solid #00274c; }
.dp__cell--selected { background: #00274c !important; color: #fff !important; }
.dp__cell--preview { outline: 2px solid #00274c; outline-offset: -2px; border-radius: 10px; }
.dp__cell--disabled { color: #cbd5e1; cursor: not-allowed; }

/* Year view */
.dp__years-wrap {
  display: flex;
  flex-direction: column;
  align-items: stretch;
}
.dp__years-nav {
  appearance: none;
  border: none;
  background: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  border-radius: 6px;
}
.dp__years-nav:hover {
  background: #f1f5f9;
  color: #475569;
}
.dp__years {
  display: grid; grid-template-columns: repeat(3, 1fr);
  gap: 4px; padding: 4px 0;
}
.dp__year {
  appearance: none; border: none; background: none;
  padding: 8px; border-radius: 10px; font-size: 13px;
  font-weight: 600; color: #1e293b; cursor: pointer;
}
.dp__year:hover { background: #f1f5f9; }
.dp__year--active { background: #00274c !important; color: #fff !important; }

/* ══════ DARK THEME ══════ */
.theme--dark .dp__input { border-color: #2a2c3a; }
.theme--dark .dp__input:hover { border-color: #3a3d4e; }
.theme--dark .dp:focus-within .dp__input { border-color: #ffd451; }

.theme--dark .dp__field { color: #e4e5eb; }
.theme--dark .dp__label { background: #111318; color: #6b7280; }
.theme--dark .dp__label--up { color: #ffd451; }

.theme--dark .dp__dropdown {
  background: #1a1b23; border-color: #2a2c3a;
  box-shadow: 0 12px 40px rgba(0,0,0,.4);
}
.theme--dark .dp__arrow { color: #8b8d9e; }
.theme--dark .dp__arrow:hover { background: #22232e; }
.theme--dark .dp__title { color: #e4e5eb; }
.theme--dark .dp__title:hover { background: #22232e; }
.theme--dark .dp__weekdays span { color: #6b7280; }
.theme--dark .dp__cell { color: #e4e5eb; }
.theme--dark .dp__cell:hover:not(.dp__cell--empty):not(.dp__cell--disabled):not(.dp__cell--selected) { background: #22232e; }
.theme--dark .dp__cell--today { border-color: #ffd451; }
.theme--dark .dp__cell--selected { background: #ffd451 !important; color: #111318 !important; }
.theme--dark .dp__cell--preview { outline-color: #ffd451; }
.theme--dark .dp__cell--disabled { color: #3a3d4e; }
.theme--dark .dp__years-nav { color: #6b7280; }
.theme--dark .dp__years-nav:hover { background: #22232e; color: #e4e5eb; }
.theme--dark .dp__year { color: #e4e5eb; }
.theme--dark .dp__year:hover { background: #22232e; }
.theme--dark .dp__year--active { background: #ffd451 !important; color: #111318 !important; }
</style>
